import { requisicaoAutenticada } from './http';

/**
 * As automações do CRM (D-016) — espelha `apps/api/src/modules/crm/automacoes`.
 * O cardápio (gatilhos, condições, ações) é o mesmo do servidor; a validação
 * de verdade é a dele.
 */

export const SERVICOS_DA_AUTOMACAO = ['RECORRENTE', 'PASSIVO', 'PONTUAL'] as const;
export type ServicoDaAutomacao = (typeof SERVICOS_DA_AUTOMACAO)[number];

export const ROTULO_DO_SERVICO: Readonly<Record<ServicoDaAutomacao, string>> = {
  RECORRENTE: 'Recorrente',
  PASSIVO: 'Passivo',
  PONTUAL: 'Pontual',
};

export const GATILHOS = [
  'SIMULACAO',
  'CONTRATO_FECHADO',
  'NEGOCIACAO_CONCLUIDA',
  'CARD_NA_COLUNA',
] as const;
export type TipoDeGatilho = (typeof GATILHOS)[number];

export const ROTULO_DO_GATILHO: Readonly<Record<TipoDeGatilho, string>> = {
  SIMULACAO: 'Simulação ou negociação criada',
  CONTRATO_FECHADO: 'Contrato fechado',
  NEGOCIACAO_CONCLUIDA: 'Negociação concluída',
  CARD_NA_COLUNA: 'Card entrou numa coluna',
};

export const AJUDA_DO_GATILHO: Readonly<Record<TipoDeGatilho, string>> = {
  SIMULACAO:
    'Negociação do Pontual criada, contrato do Passivo em simulação, simulação enviada por e-mail, pedido de fechamento do assessor, cálculo do Recorrente.',
  CONTRATO_FECHADO:
    'Pontual fechado (contrato assinado), Passivo ativado, contrato do Recorrente em Fechado.',
  NEGOCIACAO_CONCLUIDA:
    'Pontual pago (todas as parcelas baixadas, inclusive o honorário) ou Passivo finalizado.',
  CARD_NA_COLUNA:
    'Um card entrou na coluna escolhida: arrastado, criado nela ou levado por outra automação.',
};

export const ACOES = [
  'CRIAR_CARD',
  'LEVAR_CARD',
  'COPIAR_CARD',
  'ETIQUETAR',
  'COMENTAR',
  'CRIAR_ATIVIDADE',
  'ENVIAR_EMAIL',
] as const;
export type TipoDeAcao = (typeof ACOES)[number];

export const ROTULO_DA_ACAO: Readonly<Record<TipoDeAcao, string>> = {
  CRIAR_CARD: 'Pôr a empresa num quadro',
  LEVAR_CARD: 'Levar o card até uma coluna',
  COPIAR_CARD: 'Copiar o card para outro quadro',
  ETIQUETAR: 'Pôr uma tag',
  COMENTAR: 'Comentar no card',
  CRIAR_ATIVIDADE: 'Criar uma atividade',
  ENVIAR_EMAIL: 'Enviar um e-mail',
};

export const AJUDA_DA_ACAO: Readonly<Record<TipoDeAcao, string>> = {
  CRIAR_CARD: 'Cria o card na coluna. Se a empresa já está nesse quadro, a regra termina aqui.',
  LEVAR_CARD:
    'Cria o card se não houver, adianta se estiver antes. Já na coluna ou depois: a regra termina aqui.',
  COPIAR_CARD:
    'Leva a empresa à coluna de outro quadro, com as tags e quem acompanha. Só com o gatilho "card entrou numa coluna".',
  ETIQUETAR: 'Uma tag do CRM, ou a do serviço de onde veio o evento.',
  COMENTAR: 'Um texto modelo, com os campos entre chaves.',
  CRIAR_ATIVIDADE: 'Com prazo contado do dia do evento.',
  ENVIAR_EMAIL:
    'Um dos seus e-mails (Configurações › E-mails), para os contatos, o assessor ou endereços fixos. Não precisa de card.',
};

/** Para quem vai o e-mail da automação (D-017). */
export interface ParaQuem {
  readonly contatos: 'TODOS' | 'FUNCAO' | 'NENHUM';
  readonly funcao: string | null;
  readonly assessor: boolean;
  readonly enderecos: readonly string[];
}

/** As funções mais cadastradas nos contatos — sugestões do campo. */
export const FUNCOES_SUGERIDAS = [
  'Financeiro',
  'Decisor',
  'Contábil',
  'Fiscal',
  'Diretor',
] as const;

export const TIPOS_DE_ATIVIDADE = ['LIGACAO', 'EMAIL', 'REUNIAO', 'DOCUMENTO', 'TAREFA'] as const;
export const ROTULO_DO_TIPO_DE_ATIVIDADE: Readonly<
  Record<(typeof TIPOS_DE_ATIVIDADE)[number], string>
> = {
  LIGACAO: 'Ligação',
  EMAIL: 'E-mail',
  REUNIAO: 'Reunião',
  DOCUMENTO: 'Documento',
  TAREFA: 'Tarefa',
};

/** Os campos que o texto do comentário e o título da atividade aceitam. */
export const VARIAVEIS_DO_TEXTO: readonly { readonly chave: string; readonly ajuda: string }[] = [
  { chave: 'empresa', ajuda: 'a razão social' },
  { chave: 'servico', ajuda: 'Recorrente, Passivo ou Pontual' },
  { chave: 'evento', ajuda: 'o gatilho' },
  { chave: 'valor', ajuda: 'o valor, em reais' },
  { chave: 'data', ajuda: 'a data do evento' },
  { chave: 'regra', ajuda: 'o nome da automação' },
];

export const TAG_DO_SERVICO = 'SERVICO';

export type AcaoDaRegra =
  | { readonly tipo: 'CRIAR_CARD' | 'LEVAR_CARD' | 'COPIAR_CARD'; readonly colunaId: string | null }
  | { readonly tipo: 'ETIQUETAR'; readonly tagId: string }
  | { readonly tipo: 'COMENTAR'; readonly texto: string }
  | {
      readonly tipo: 'CRIAR_ATIVIDADE';
      readonly titulo: string;
      readonly tipoAtividade: (typeof TIPOS_DE_ATIVIDADE)[number];
      readonly prazoEmDias: number;
      readonly responsavelId: string | null;
    }
  | {
      readonly tipo: 'ENVIAR_EMAIL';
      readonly emailId: string;
      readonly para: ParaQuem;
      /** 0 = na hora; senão, dias depois, às 9h. */
      readonly diasDepois: number;
    };

export interface DefinicaoDaRegra {
  readonly gatilho: { readonly tipo: TipoDeGatilho; readonly colunaId?: string | null };
  readonly condicoes: {
    readonly servicos?: readonly ServicoDaAutomacao[];
    readonly somenteNegocioNovo?: boolean;
    readonly valorMinimo?: number;
  };
  readonly acoes: readonly AcaoDaRegra[];
}

export interface ExecucaoDaAutomacao {
  readonly quando: string;
  readonly evento: string;
  readonly empresa: string | null;
  readonly resultado: 'APLICADA' | 'SEM_EFEITO' | 'FALHOU';
  readonly detalhe: string;
}

export interface Automacao {
  readonly id: string;
  readonly nome: string;
  readonly pedido: string | null;
  readonly ativa: boolean;
  readonly padrao: boolean;
  readonly definicao: DefinicaoDaRegra | null;
  readonly descricao: string;
  readonly situacao: 'ATIVA' | 'DESLIGADA' | 'PARADA';
  readonly problemas: readonly string[];
  readonly ultimaExecucao: ExecucaoDaAutomacao | null;
  readonly execucoesNoMes: number;
  readonly atualizadaEm: string;
}

export interface OpcoesDasAutomacoes {
  readonly quadros: readonly {
    readonly id: string;
    readonly nome: string;
    readonly colunas: readonly { readonly id: string; readonly nome: string }[];
  }[];
  readonly tags: readonly {
    readonly id: string;
    readonly nome: string;
    readonly deServico: boolean;
  }[];
  readonly pessoas: readonly { readonly accountId: string; readonly nome: string }[];
  /** Os e-mails criados em Configurações › E-mails. */
  readonly emails: readonly { readonly id: string; readonly nome: string }[];
}

/** O que a IA entendeu do texto — ou por que não deu para montar a regra. */
export type Interpretacao =
  | {
      readonly ok: true;
      readonly nome: string;
      readonly entendimento: string;
      readonly definicao: DefinicaoDaRegra;
      /** Como o Deca vai executar — a descrição do servidor, não a da IA. */
      readonly descricao: string;
      readonly problemas: readonly string[];
    }
  | { readonly ok: false; readonly motivo: string };

export const CHAVE_DAS_AUTOMACOES = ['crm', 'automacoes'] as const;

export const carregarAutomacoes = (): Promise<Automacao[]> =>
  requisicaoAutenticada<Automacao[]>('/crm/automacoes');

export const carregarOpcoesDasAutomacoes = (): Promise<OpcoesDasAutomacoes> =>
  requisicaoAutenticada<OpcoesDasAutomacoes>('/crm/automacoes/opcoes');

export const descreverAutomacao = (
  definicao: DefinicaoDaRegra,
): Promise<{ descricao: string; problemas: string[] }> =>
  requisicaoAutenticada('/crm/automacoes/descricao', { metodo: 'POST', corpo: { definicao } });

export const criarAutomacao = (dados: {
  readonly nome: string;
  readonly definicao: DefinicaoDaRegra;
  readonly ativa?: boolean;
  readonly pedido?: string | null;
}): Promise<Automacao> =>
  requisicaoAutenticada<Automacao>('/crm/automacoes', { metodo: 'POST', corpo: dados });

export const editarAutomacao = (
  id: string,
  dados: {
    readonly nome?: string;
    readonly definicao?: DefinicaoDaRegra;
    readonly ativa?: boolean;
    readonly pedido?: string | null;
  },
): Promise<Automacao> =>
  requisicaoAutenticada<Automacao>(`/crm/automacoes/${id}`, { metodo: 'PATCH', corpo: dados });

export const excluirAutomacao = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/automacoes/${id}`, { metodo: 'DELETE' });

export const carregarExecucoes = (id: string): Promise<ExecucaoDaAutomacao[]> =>
  requisicaoAutenticada<ExecucaoDaAutomacao[]>(`/crm/automacoes/${id}/execucoes`);

/** A IA lê o texto e propõe a regra; com `atual`, corrige a regra que já existe. */
export const interpretarAutomacao = (dados: {
  readonly texto: string;
  readonly atual?: { readonly nome: string; readonly definicao: DefinicaoDaRegra } | undefined;
}): Promise<Interpretacao> =>
  requisicaoAutenticada<Interpretacao>('/crm/automacoes/interpretacao', {
    metodo: 'POST',
    corpo: dados,
  });

/** Uma regra vazia para o "Criar sem IA": o gatilho mais comum e uma ação a escolher. */
export function regraEmBranco(): DefinicaoDaRegra {
  return {
    gatilho: { tipo: 'CONTRATO_FECHADO' },
    condicoes: {},
    acoes: [{ tipo: 'LEVAR_CARD', colunaId: null }],
  };
}

/** A ação nova de um tipo, com os campos em branco. */
export function acaoEmBranco(tipo: TipoDeAcao): AcaoDaRegra {
  switch (tipo) {
    case 'CRIAR_CARD':
    case 'LEVAR_CARD':
    case 'COPIAR_CARD':
      return { tipo, colunaId: null };
    case 'ETIQUETAR':
      return { tipo, tagId: '' };
    case 'COMENTAR':
      return { tipo, texto: '' };
    case 'CRIAR_ATIVIDADE':
      return {
        tipo,
        titulo: '',
        tipoAtividade: 'TAREFA',
        prazoEmDias: 1,
        responsavelId: null,
      };
    case 'ENVIAR_EMAIL':
      return { tipo, emailId: '', para: PARA_PADRAO, diasDepois: 0 };
  }
}

/** Para quem, de saída: todos os contatos da empresa. */
export const PARA_PADRAO: ParaQuem = {
  contatos: 'TODOS',
  funcao: null,
  assessor: false,
  enderecos: [],
};

/** A regra que nasce na página de um e-mail: o gatilho mais comum e a ação de mandá-lo. */
export function regraDoEmail(emailId: string): DefinicaoDaRegra {
  return {
    gatilho: { tipo: 'CONTRATO_FECHADO' },
    condicoes: {},
    acoes: [{ tipo: 'ENVIAR_EMAIL', emailId, para: PARA_PADRAO, diasDepois: 0 }],
  };
}
